<template>
  <mf-loading :boolean-loader="isLoading" loader-type="dialog">
    <v-card class="import-clients" elevation="0">
      <v-card-title>
        <span class="section-title pt-3">{{ title }}</span>
        <v-spacer />
        <slot name="custom-button" />
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="4">
            <p class="subtitle mb-1">Primeiramente selecione o arquivo CSV em seu computador.</p>

            <v-container fluid class="pa-0 import-file-block">
              <v-row>
                <v-col class="import-col" @click="activateInput">
                  <mf-icon fill color="marca-0-l40"> cloud_upload </mf-icon>
                  <span class="ml-2 text import">{{ fileName ? fileName : importLabel }}</span>
                  <p class="text import-hint mb-1">Somente formato CSV.</p>

                  <input id="dynamic-file-input" ref="file-input" class="hidden-input" type="file" @input="readFile($event)" />
                </v-col>
              </v-row>
            </v-container>

            <p class="texto-corrido">Em seguida, selecione a coluna desejada ao lado e nos diga em qual categoria ela se encaixa.</p>
            <p class="texto-corrido mb-1">
              {{ listText }}
            </p>

            <ul class="mb-5">
              <li v-for="(field, index) in fields" :key="index">
                <p :class="field.subLabel ? 'mb-0' : 'mb-1'" class="texto-corrido">{{ field.text }}</p>
                <span v-if="field.subLabel" class="field-sublabel">{{ field.subLabel }}</span>
              </li>
            </ul>

            <mf-info-card
              v-if="cashback"
              type="info"
              title="Importante!"
              class="info-card"
              description="Para adicionar cashback a carteira do consumidor, o valor deve ser positivo (Ex: 10,00)."
            >
              <p class="mb-0">Para remover cashback a carteira do consumidor, o valor deve ser negativo (Ex: -10,00)</p>
            </mf-info-card>

            <p v-if="isMariaCampaign" class="texto-corrido">*Por questões de performance são permitidos arquivos com até 1000 produtos.</p>

            <div v-for="(cfgOpt, index) of booleanConfigurationOptions" :key="`opt-${index}`" class="d-flex align-center mf-toggle-container">
              <mf-toggle
                v-model="booleanConfigurationOptionsValues[cfgOpt.value]"
                class="pa-0"
                dense
                hide-details
                :label="!cfgOpt.tooltip ? cfgOpt.label : ''"
              />
              <v-tooltip v-if="cfgOpt.tooltip" right max-width="254px">
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <span class="texto-corrido">{{ cfgOpt.label }}</span
                    ><mf-icon small class="align-center pl-2"> info </mf-icon>
                  </div>
                </template>
                <span>{{ cfgOpt.tooltip }}</span>
              </v-tooltip>
            </div>

            <div v-if="isOfferSales" class="d-flex align-center">
              <mf-icon fill size="22" color="vermelho-l20">warning</mf-icon>
              <p class="warning-text">
                Formatar dados como números pode ocasionar a remoção de zeros a esquerda e também abreviações incorretas. Formate seus campos como texto.
              </p>
            </div>
          </v-col>
          <v-col cols="8" justify="start">
            <v-row v-if="importedProductsPreview.length" class="preview-row">
              <table class="preview-table">
                <thead>
                  <tr class="custom-th">
                    <th v-for="(column, columnIndex) in importedProductsPreview[0]" :key="columnIndex" class="px-1 pb-2 custom-td">
                      <v-select
                        v-model="selectedColumns[columnIndex]"
                        hide-details
                        solo
                        dense
                        flat
                        :items="fieldsWithDisabled"
                        background-color="cinza-l90"
                        color="cinza-l30"
                        clearable
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, lineIndex) in importedProductsPreview" :key="lineIndex" class="custom-th">
                    <td v-for="(element, elementIndex) in line" :key="elementIndex" class="custom-td">
                      {{ element }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-row>
            <v-container v-else class="pa-0 preview-col" fill-height>
              <v-row justify="center">
                <span>Aguardando arquivo em .csv</span>
              </v-row>
            </v-container>
            <div v-if="importedProductsPreview && importedProductsPreview.length > 0" class="mt-2 d-flex align-center preview-info">
              <mf-icon dense color="cinza-0" icon="info" class="mr-1" />
              <p class="mb-0">Apenas as 20 primeiras linhas são exibidas no preview.</p>
            </div>
          </v-col>
        </v-row>
        <mf-action-buttons
          v-if="showActions"
          :extra-button="{
            text: 'Cancelar',
            action: closeDialog,
            isVisible: true,
          }"
          :primary-button="{
            text: 'Importar',
            action: finish,
            isVisible: true,
          }"
        />
      </v-card-text>
    </v-card>
  </mf-loading>
</template>

<script>
import CSV from 'csv-string'
import { localeString } from '@/config/helpers.js'
const PRODUCTS_LIMIT = 1001
export default {
  name: 'ImportDynamicCsvCard',
  inject: ['objectSelected', 'updateRequiredColumns'],
  props: {
    isModalOpen: Boolean,
    showActions: {
      type: Boolean,
      default: true,
    },
    returnDefaultFile: {
      type: Boolean,
      default: true,
    },
    customFields: {
      type: Boolean,
      default: false,
    },
    logicText: {
      type: String,
      default: 'e',
    },
    title: {
      type: String,
      default: 'Importar arquivo .csv',
    },
    importLabel: {
      type: String,
      required: true,
    },
    listText: {
      type: String,
      default: 'Valores que podem ser importados:',
    },
    customFieldsWithDisabled: {
      type: Array,
      default: () => [],
    },
    requiredAnyOf: {
      type: Array,
      required: false,
      default: () => ['sku', 'ean'],
    },
    requiredAllOf: {
      type: Array,
      required: false,
      default: () => [],
    },
    fields: {
      type: Array,
      required: true,
      validator: (value) => {
        if (!Array.isArray(value)) return false
        if (!value.length) return false
        let haveAllFields = true
        value.forEach((obj) => {
          if (!Object.keys(obj).includes('text') || !Object.keys(obj).includes('value')) haveAllFields = false
        })
        return haveAllFields
      },
    },
    booleanConfigurationOptions: {
      type: Array,
      required: false,
      default: () => [],
      validator: (value) => {
        if (!Array.isArray(value)) return false
        let haveAllFields = true
        value.forEach((obj) => {
          if (!Object.keys(obj).includes('label')) haveAllFields = false
        })
        return haveAllFields
      },
    },
    loadObjects: {
      type: Boolean,
      default: false,
    },
    lineLimits: {
      type: Number,
      default: 0,
    },
    isOfferSales: {
      type: Boolean,
      default: false,
    },
    cashback: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    file: null,
    fileName: '',
    fileType: '',
    dataContent: [],
    contentSample: [],
    selectedColumns: [],
    booleanLoader: false,
    booleanConfigurationOptionsValues: {},
    fileTypes: [
      'text/comma-separated-values',
      'text/csv',
      'application/csv',
      'application/excel',
      'application/vnd.ms-excel',
      'application/vnd.msexcel',
      'text/anytext',
      '',
    ],
  }),
  computed: {
    heightCard() {
      return !this.showActions ? 600 : ''
    },
    isMariaCampaign() {
      return ['Edit Mar.ia campaign', 'New Mar.ia campaign'].includes(this.$route.name)
    },
    fieldsWithDisabled() {
      if (this.customFields) return this.customFieldsWithDisabled
      return this.fields.map((obj) => {
        return { ...obj, disabled: Boolean(this.selectedColumns.includes(obj.value)), text: obj.label ?? obj.text }
      })
    },
    isLoading() {
      return this.loading || this.booleanLoader
    },
    importedProductsPreview() {
      if (this.contentSample.length <= 0) return []
      return this.contentSample.slice(0, 21)
    },
  },
  watch: {
    isModalOpen(value) {
      this.setBooleanConfiguration(value)
    },
    selectedColumns(value) {
      this.updateRequiredColumns(value)
    },
    contentSample() {
      if (!this.isMariaCampaign) return

      if (this.contentSample.length >= PRODUCTS_LIMIT) {
        this.contentSample = []
        this.$snackbar({ message: 'Não são permitidos arquivos com mais de 1000 produtos.', snackbarColor: 'error' })
      }
    },
  },
  mounted() {
    this.setBooleanConfiguration(true)
  },
  methods: {
    setBooleanConfiguration(value) {
      if (value) this.booleanConfigurationOptionsValues = this.booleanConfigurationOptions.reduce((acc, cfgOpt) => ({ ...acc, [cfgOpt.value]: false }), {})
    },
    activateInput() {
      this.$refs['file-input'].click()
      document.querySelector('#dynamic-file-input').value = '' // This enables secting the same file again
    },
    readFile(event) {
      if (event.target.files.length === 0) return
      const fileType = event.target.files[0].type.trim()
      if (!this.fileTypes.includes(fileType)) {
        this.$snackbar({ message: `Permitido apenas arquivos em .csv, o arquivo tem o formato: ${fileType}`, snackbarColor: 'error' })
        return
      }

      this.selectedColumns = []

      this.file = event.target.files[0]

      this.fileName = this.file.name
      this.fileType = this.file.type

      let reader = new FileReader()
      reader.onload = () => {
        if (this.lineLimits) {
          const lines = reader.result.split('\n').filter((line) => line.trim() !== '')
          if (lines.length > this.lineLimits) {
            this.$snackbar({
              message: `Limite de linhas excedido: São permitidos apenas arquivos com ${localeString(this.lineLimits)} linhas ou menos.`,
              snackbarColor: 'error',
            })
            return
          }
        }
        if (this.loadObjects) {
          this.dataContent = CSV.parse(reader.result)
          this.contentSample = this.dataContent
        } else {
          this.contentSample = CSV.parse(reader.result)
        }
      }

      reader.readAsText(this.file)
    },
    closeDialog() {
      this.fileName = ''
      this.fileType = ''
      this.file = null
      this.dataContent = []
      this.contentSample = []
      this.selectedColumns = []
      this.$emit('close-dialog', false)
    },
    async finish() {
      if (this.requiredAnyOf.length && !this.selectedColumns.reduce((acc, col) => acc || this.requiredAnyOf.includes(col), false)) {
        const mandatoryProducts = this.requiredAnyOf.map((col) => this.fields.find((field) => field.value === col).text)
        let columnsLabel = ''
        if (mandatoryProducts.length === 1) {
          columnsLabel = mandatoryProducts[0]
        } else {
          columnsLabel = `${mandatoryProducts.slice(0, mandatoryProducts.length - 1).join(', ')} ou ${mandatoryProducts[mandatoryProducts.length - 1]}`
        }
        this.$snackbar({
          message: `É necessário importar no mínimo uma das colunas: ${columnsLabel}`,
          snackbarColor: 'error',
        })
        return
      }

      if (this.requiredAllOf.length && !this.requiredAllOf.reduce((acc, col) => acc && this.selectedColumns.includes(col), true)) {
        const mandatoryProducts = this.requiredAllOf.map((col) => this.fields.find((field) => field.value === col).text)
        let columnsLabel = ''
        if (mandatoryProducts.length === 1) {
          columnsLabel = mandatoryProducts[0]
        } else {
          columnsLabel = `${mandatoryProducts.slice(0, mandatoryProducts.length - 1).join(', ')} ${this.logicText}
            ${mandatoryProducts[mandatoryProducts.length - 1]}`
        }
        this.$snackbar({
          message: `É necessário importar todas as colunas: ${columnsLabel}`,
          snackbarColor: 'error',
        })
        return
      }

      let event = {}

      if (this.returnDefaultFile) {
        event = {
          file: this.file,
          selectedColumns: this.selectedColumns,
          fileName: this.fileName,
          booleanConfigurationOptionsValues: this.booleanConfigurationOptionsValues,
        }
      } else {
        event = { file: this.contentSample, selectedColumns: this.selectedColumns }
      }
      if (this.loadObjects) {
        // Trim each cell to remove whitespaces
        event.objects = this.dataContent.map((row) => this.selectedColumns.reduce((acc, column, index) => ({ ...acc, [column]: row[index].trim(' ') }), {}))
      }
      this.booleanLoader = true
      await this.objectSelected(event)
      if (!this.cashback) this.closeDialog()
      this.booleanLoader = false
    },
  },
}
</script>

<style lang="scss" scoped>
.import-clients {
  .warning-text {
    margin: 0 0 0 10px;
    font-weight: 400;
    font-size: 12px;
    color: $cinza-0 !important;
    line-height: 120%;
  }

  .import-col {
    cursor: pointer;
    user-select: none;
  }
  .hidden-input {
    display: none;
  }
  .text {
    opacity: 0.87;
    max-width: 300px;
    &.import {
      color: $marca-0-l40;
    }
    &.import-hint {
      font-size: 12px;
      opacity: 0.54;
    }
  }
  .category-box {
    .select-label {
      opacity: 0.87;
    }
    .disabled-opacity {
      opacity: 0.54;
    }
  }
  .preview-col {
    background-color: $cinza-l90;
    border-radius: 4px;
    border: 1px solid $cinza-l30;
    padding: 2px;
  }

  .preview-row {
    overflow: auto;
    max-height: 295px;
    max-width: 750px;
  }

  table.preview-table {
    background-color: $cinza-l90;
    max-width: 580px;
    text-align: left;
    border-collapse: collapse;
    border: 1px solid $cinza-l30;
  }
  table.preview-table td,
  table.preview-table th {
    border: 1px solid $cinza-l30;
    padding: 10px 10px;
  }
  table.preview-table thead {
    background: $cinza-l90;
    border-bottom: 3px solid $cinza-l30;
  }
  table.preview-table thead th {
    text-align: left;
  }

  .mf-toggle-container {
    .v-input--selection-controls {
      margin: 0 !important;
      padding: 0 !important;
    }
  }
}

::v-deep .info-card p {
  font-weight: 400;
  color: $marca-0-l40 !important;
}

.preview-info {
  p {
    color: $cinza-0;
  }
}

.field-sublabel {
  font-size: 12px;
  color: $cinza-d10;
  font-weight: bold;
}
</style>
